import { useFetchContratos, useFetchSectors, useGetContratoParaTickets } from "@/shared/actions/erp";
import { useResolverContratosErp } from "@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-clientes.actions";
import { Contrato, Sector } from "@/shared/interfaces";
import { IVision360Cliente } from "@/shared/interfaces/erp/tickets-soporte-clientes";
import { Alert, Box, Fade } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import VisitaForm from "../OrdenServicioVisita/VisitaForm";

import { useLoaders } from "@/shared";

export type ServiceTypeOrdenServicio =
    | "TRASLADO"
    | "VISITA"
    | "NO_REQUIERE"
    | null;

type OrdenServicoProps = {
    form: any;
    cliente: IVision360Cliente;
}
export default function OrdenServico({ form, cliente }: OrdenServicoProps) {
    //state
    const [contrato, setContrato] = useState<Contrato | null>(null);
    const [sector, setSector] = useState<Sector | null>(null);
    const [serviceType, setServiceType] = useState<ServiceTypeOrdenServicio>(null);

    //form
    const { data: dataContrato, isLoading: isLoadingContrato } =
        useFetchContratos({
            params: {
                identificacion: cliente.identificacion,
            },
        });

    const directContratos: Contrato[] = (dataContrato as any)?.data?.data ?? [];

    const requiereVisita = form.watch("requiere_visita");

    // Fallback (clientes antiguos): si Django no devolvio contratos directos,
    // el micro resuelve el id_contrato via api_preagendamiento.id_instalacion_externa.
    const needsResolver =
        requiereVisita && !isLoadingContrato && directContratos.length === 0;

    const { data: dataResolver, isLoading: isLoadingResolver } =
        useResolverContratosErp(cliente?.identificacion ?? "", needsResolver);

    // ApiResponse<T> ya es { code, status, message, data: T }, asi que
    // el array vive en .data directamente (no en .data.data).
    const resolverItems = (dataResolver as any)?.data ?? [];
    const erpIdResolved: string | null = useMemo(() => {
        const hit = resolverItems.find(
            (r: any) => r?.erp_id_contrato && r?.fuente !== "NO_ENCONTRADO"
        );
        return hit?.erp_id_contrato ?? null;
    }, [resolverItems]);

    // Endpoint dedicado al modulo tickets: bypass de las reglas de rol del ERP
    // que esconden contratos importados de Softnet (sin user_create / canal_venta).
    const { data: dataContratoResolved, isLoading: isLoadingContratoResolved } =
        useGetContratoParaTickets(erpIdResolved ?? "", !!erpIdResolved);

    const resolvedContrato: Contrato | null = useMemo(() => {
        if (directContratos.length > 0) return directContratos[0];
        // /para-tickets/ devuelve ApiResponse<Contrato>: el contrato vive en .data.
        const resolved = (dataContratoResolved as any)?.data ?? null;
        return resolved && resolved.id_contrato ? (resolved as Contrato) : null;
    }, [directContratos, dataContratoResolved]);

    const resolverFinalizado =
        requiereVisita &&
        !isLoadingContrato &&
        !isLoadingResolver &&
        !isLoadingContratoResolved;

    const noContratoEncontrado =
        resolverFinalizado &&
        directContratos.length === 0 &&
        !erpIdResolved;

    const {
        isLoading: isLoadingSector,
        data: dataSector
    } = useFetchSectors({
        id_sector: contrato?.sector_data?.id_sector,
        page_size: 600,
        estado: true,
    }, !!contrato?.sector_data?.id_sector)

    useEffect(() => {
        if (requiereVisita) {
            setServiceType("VISITA");
            form.setValue("tipo_orden_servicio", "VISITA");
            setContrato(resolvedContrato);
            form.setValue("contrato_id", resolvedContrato?.id_contrato || null);
        } else {
            setServiceType(null);
            form.setValue("tipo_orden_servicio", null);
            form.setValue("contrato_id", null);
            setContrato(null)
            setSector(null);
        }
    }, [requiereVisita, resolvedContrato]);

    useEffect(() => {
        if (dataSector?.data.data.length) {
            setSector(dataSector?.data?.data[0]);
            form.setValue("id_zona", dataSector?.data?.data[0]?.zona_data?.id_zona || "");
        }
    }, [dataSector]);

    useLoaders(isLoadingContrato || isLoadingSector || isLoadingResolver || isLoadingContratoResolved);

    return (
        <Box sx={{ px: 3, pb: 3 }}>
            {noContratoEncontrado && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No se encontro un contrato vinculable para este cliente
                    (ni por numero de referencia ni por preagendamiento).
                    Verifica la sincronizacion del cliente con Softnet.
                </Alert>
            )}
            {contrato && serviceType === "VISITA" && (
                <Fade in={serviceType === "VISITA"}>
                    <Box>
                        <VisitaForm
                            contrato={contrato}
                            sector={sector}
                            form={form}
                        />
                    </Box>
                </Fade>
            )}
        </Box>
    )
}
